import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Card, CardBody, Col, Row,
} from 'reactstrap';
import Select from 'react-select';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Translate from '@securitize/ui-components/i18n/Translate/Translate';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import './SmallCalculator.scss';
import CurrencyToSymbol from '../../../CurrencyToSymbol';
import AutoNumericInput from '../../../AutoNumericInput';

const OnFormSubmitFinish = ({ onFinish }) => {
  const { isSubmitting } = useFormikContext();
  useEffect(() => {
    if (!isSubmitting) onFinish();
  }, [isSubmitting]);

  return null;
};

OnFormSubmitFinish.propTypes = {
  onFinish: PropTypes.func.isRequired,
};

const SmallCalculator = (props) => {
  const {
    initValue, initialCurrency, currencyList, onSave, xs, md, isTokenCalculatorVisible,
    editDisabled, tokenDecimals,
  } = props;
  const initCurrency = currencyList.find(({ name }) => name === initialCurrency);
  const [isEditing, setIsEditing] = useState(false);
  const isCrypto = (currency) => ['BTC', 'ETH', 'BCH'].includes((currency || '').toUpperCase());

  const calculateBonusPercent = (amount, tiers) => {
    const bonus = (tiers || [])
      .sort((a, b) => b.minInvestmentAmount - a.minInvestmentAmount)
      .find(({ minInvestmentAmount }) => minInvestmentAmount < amount);
    return bonus ? bonus.bonusPercentage / 100 : 0;
  };

  const ref = useRef();

  const validationSchema = Yup.object().shape({
    minAmount: Yup.number(),
    name: Yup.string(),
    investValue: Yup.number().min(Yup.ref('minAmount'))
      .typeError('Please enter a value')
      .required('Please enter a value'),
  });

  const validate = (values) => {
    try {
      validationSchema.validateSync(values, { abortEarly: false });
      return {};
    } catch (error) {
      const decimalPlaces = isCrypto(values.name) ? 4 : 2;
      return {
        investValue:
  <span>
    <span>Minimum investment amount is</span>
    <span>&nbsp;</span>
    <CurrencyToSymbol currency={values.name} />
    <span>
      {` ${Number(values.minAmount)
        .toLocaleString(undefined, {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        })}`}
    </span>
  </span>
        ,
      };
    }
  };
  const currencyToLabel = (currency) => (
    <span className="small-calculator__currency">
      <CurrencyToSymbol hideUnResolvedCurrency currency={currency} />
      <span>&nbsp;</span>
      <span>{currency}</span>
    </span>
  );

  return (
    <Formik
      initialValues={{ investValue: initValue, ...(initCurrency || currencyList[0] || {}) }}
      validateOnMount
      enableReinitialize
      validate={validate}
      onSubmit={(values, formikBag) => {
        onSave({ ...values, isValid: true }, formikBag);
      }}
    >
      {(formikBag) => {
        const convertedAmount = formikBag.values.investValue * formikBag.values.value || 0;
        const investValue = parseFloat(convertedAmount.toFixed(tokenDecimals));
        const bonusValue = parseFloat(
          (calculateBonusPercent(formikBag.values.investValue, formikBag.values.tiers) * investValue)
            .toFixed(tokenDecimals),
        );
        const decimalPlaces = isCrypto(formikBag.values.name) ? 4 : 2;

        return (
          <Col
            xs={xs}
            md={md}
            className={classnames('p-0', {
              'h-50': !isTokenCalculatorVisible,
            })}
          >

            <Card className="small-calculator m-0 border-0">
              <OnFormSubmitFinish onFinish={() => {
                formikBag.resetForm();
                setIsEditing(false);
              }}
              />
              <Row className="pt-3 pb-0 pl-3 pr-3">
                <Col className="d-flex justify-content-end">
                  {
                    isEditing
                      ? (
                        <div>
                          <Button
                            id="btn-cancel"
                            className="m-0 p-0 text-white font-16 mr-3"
                            color="link"
                            size="sm"
                            disabled={formikBag.isSubmitting}
                            onClick={() => {
                              formikBag.resetForm();
                              setIsEditing(false);
                            }}
                          >
                            <Translate translationKey="Texts.cancel" />
                          </Button>
                          <Button
                            id="btn-save"
                            className="m-0 p-0 text-white font-16"
                            color="link"
                            size="sm"
                            disabled={!formikBag.isValid || formikBag.isSubmitting}
                            onClick={(e) => {
                              formikBag.handleSubmit(e);
                              formikBag.setSubmitting(true);
                            }}
                          >
                            <i className="icon-check mr-1" />
                            <Translate translationKey="Texts.save" />
                          </Button>
                        </div>
                      )
                      : (
                        !editDisabled && (
                        <Button
                          id="btn-edit-amount"
                          className="m-0 p-0 text-white font-16"
                          color="link"
                          size="sm"
                          onClick={() => {
                            setIsEditing(true);
                            window.setTimeout(() => ref.current && ref.current.focus());
                          }}
                        >
                          <Translate translationKey="Texts.edit" />
                        </Button>
                        )
                      )
                  }
                </Col>
              </Row>
              <CardBody className="small-calculator__card-body p-3 pt-3 h-100">
                <div className="font-14 mb-2">
                  <Translate translationKey="Texts.you-invest" />
                </div>
                <Row>
                  {
                    editDisabled
                      ? (
                        <Col>
                          <div className="font-32 ml-0">
                            <span id="span-shares-total" className="mr-2">
                              {
                              (formikBag.values.investValue)
                                .toLocaleString(undefined, {
                                  minimumFractionDigits: decimalPlaces,
                                  maximumFractionDigits: decimalPlaces,
                                })
                            }
                            </span>
                            <span className="font-12 font-weight-light">{formikBag.values.name}</span>
                          </div>
                        </Col>
                      )
                      : (
                        <>
                          <Col xs={8}>
                            <div className={classnames('small-calculator__input-wrapper h-100', {
                              'small-calculator__input-wrapper--invalid': !formikBag.isValid && formikBag.touched.investValue,
                            })}
                            >
                              <AutoNumericInput
                                decimalPlaces={decimalPlaces}
                                ref={ref}
                                disabled={!isEditing}
                                className="h-100 font-weight-semi border-0 p-0 text-white font-32"
                                onChange={(value) => {
                                  formikBag.setTouched({ investValue: true });
                                  formikBag.setFieldValue('investValue', value);
                                }}
                                value={formikBag.values.investValue}
                              />
                            </div>
                          </Col>
                          <Col xs={4} className="pl-0">
                            <Select
                              id="currency-select"
                              arrowRenderer={({ isOpen }) => <i className={!isOpen ? 'icon-point-down' : 'icon-point-up'} />}
                              required
                              className="small-calculator__select"
                              value={{ label: currencyToLabel(formikBag.values.name) }}
                              onChange={({ value }) => {
                                formikBag.setFieldValue('minAmount', value.minAmount);
                                formikBag.setFieldValue('value', value.value);
                                formikBag.setFieldValue('tiers', value.tiers);
                                formikBag.setFieldValue('oneShareValue', value.oneShareValue);
                                window.setTimeout(() => formikBag.setFieldValue('name', value.name));
                              }}
                              options={currencyList.map((item) => ({
                                label: currencyToLabel(item.name),
                                value: item,
                              }))}
                              disabled={!isEditing || formikBag.isSubmitting}
                              clearable={false}
                              searchable={false}
                            />
                          </Col>
                        </>
                      )
                  }
                </Row>
                {isTokenCalculatorVisible && (
                  <Row>
                    <Col>
                      <div className="font-14 mt-3">
                        <Translate translationKey="Texts.you-receive" />
                      </div>
                      <div className="font-32 pt-2 ml-0">
                        <span id="span-shares-total" className="mr-2">
                          {(investValue + bonusValue).toLocaleString(undefined, { maximumFractionDigits: tokenDecimals })}
                        </span>
                        <span className="font-12 font-weight-light"><Translate translationKey="Texts.tokens" /></span>
                      </div>
                      <div id="shares-usd-received-label" className="pt-2 font-12 font-weight-semi">
                        <span>1</span>
                          &nbsp;
                        <Translate translationKey="Texts.share" />
                        <span>{` = ${formikBag.values.oneShareValue} ${formikBag.values.name}`}</span>
                      </div>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
            {
              !editDisabled && (
                <div className="ml-2 mt-2 position-absolute">
                  {
                    !formikBag.isValid && formikBag.touched.investValue
                      ? <span className="font-weight-bolder font-12 text-danger">{formikBag.errors.investValue}</span>
                      : (
                        <span className="font-weight-light font-12">
                          <Translate translationKey="Texts.on-change-sign-again" />
                        </span>
                      )
                  }
                </div>
              )
            }
          </Col>
        );
      }}
    </Formik>
  );
};

SmallCalculator.defaultProps = {
  initValue: 0,
  onSave: () => undefined,
  initialCurrency: 'USD',
  isTokenCalculatorVisible: true,
  xs: 12,
  md: 4,
  editDisabled: false,
  tokenDecimals: 0,
};

SmallCalculator.propTypes = {
  initValue: PropTypes.number,
  xs: PropTypes.number,
  md: PropTypes.number,
  isTokenCalculatorVisible: PropTypes.bool,
  initialCurrency: PropTypes.string,
  currencyList: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    minAmount: PropTypes.number,
  })).isRequired,
  onSave: PropTypes.func,
  editDisabled: PropTypes.bool,
  tokenDecimals: PropTypes.number,
};

export default SmallCalculator;
